/** Renders the full single-tree detail view with interactive V4 nodes, branches, and an info panel. */
import Phaser from 'phaser';
import type { V4TreeDefinition, V4NodeDefinition, V4ApotheosisDefinition } from '../utils/Types';
import type { AttunementComponent } from '../components/AttunementComponent';
import type { SkillProgressionComponent } from '../components/SkillProgressionComponent';
import { ConfigLoader } from '../systems/ConfigLoader';
import { crispGraphics, crispText, crispCircle, ensureRingTexture, ensureGlowTexture, CRISP_SCALE } from './CrispUtil';
import {
  Color, Hex, Font, getTierColor, getNodeRadius,
  NODE_TIER_LABEL_ALPHA, NODE_FORK_PULSE_ALPHA,
} from './Theme';
import { TreeLayoutEngine, type LayoutTier } from './TreeLayoutEngine';
import { GAME_WIDTH } from '../utils/Constants';
import { clamp } from '../utils/Math';
import {
  buildLabelCandidates, pickBestCandidate,
  type LineSeg, type LabelPlacement, type NodeCircle,
} from './LabelPlacementEngine';
import { TreeInfoPanel } from './TreeInfoPanel';

/** Callbacks wired by the owning scene to handle node interaction events from the detail renderer. */
export interface TreeDetailCallbacks {
  onNodeClick: (treeName: string, node: V4NodeDefinition | V4ApotheosisDefinition, tier: LayoutTier) => void;
  onNodeHover: (node: (V4NodeDefinition | V4ApotheosisDefinition) | null) => void;
}

type NodeState = 'locked' | 'available' | 'unlocked' | 'equipped' | 'passive' | 'fork_available' | 'superseded';

/** Computed screen position and display state for a single node. */
interface NodeLayout {
  node: V4NodeDefinition | V4ApotheosisDefinition;
  tier: LayoutTier;
  x: number;
  y: number;
  state: NodeState;
  nodeType: string;
}

const LABEL_GAP = 3;

const TIER_LABELS: Record<string, string> = {
  foundation: 'FOUNDATION',
  threshold: 'THRESHOLD',
  transcendent: 'TRANSCENDENT',
  apotheosis: 'APOTHEOSIS',
};

/**
 * Renders a full single-tree view with V4 node types, fork highlights,
 * evolution arrows, and an info panel.
 */
export class TreeDetailRenderer {
  private scene: Phaser.Scene;
  private callbacks: TreeDetailCallbacks;
  private objects: Phaser.GameObjects.GameObject[] = [];
  private infoPanel: TreeInfoPanel | null = null;
  private hasNodeIcons = false;

  constructor(scene: Phaser.Scene, callbacks: TreeDetailCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  /** Render the full V4 tree graph inside the given bounds. */
  render(
    x: number, y: number, width: number, height: number,
    treeName: string,
    treeDef: V4TreeDefinition,
    attunement: AttunementComponent,
    skillProgression: SkillProgressionComponent,
  ): void {
    this.clear();
    const S = CRISP_SCALE;
    this.hasNodeIcons = this.scene.textures.exists('node-icons');

    // Layout all nodes
    const layouts = this.layoutNodes(treeDef, x, y, width, height, treeName, attunement, skillProgression);

    // Graphics for branches and nodes
    const g = crispGraphics(this.scene);
    this.objects.push(g);

    // XP display
    const treeXp = skillProgression.getTreeXp(treeName);
    const displayXp = Math.floor(treeXp);
    const xpText = crispText(this.scene, GAME_WIDTH / 2, y, `XP: ${displayXp}`, {
      fontFamily: Font.family, fontSize: Font.sizeSmall, color: Hex.textMuted,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5);
    this.objects.push(xpText);

    // Build the shared id→layout map once, passed to both branch methods
    const layoutMap = this.buildLayoutMap(layouts);

    // Collect branch lines for label collision avoidance
    const lines = this.collectBranchLines(layoutMap, treeDef);

    // Draw branches
    this.drawBranches(g, layoutMap, treeDef, treeXp, S);

    // Tier labels on left side
    this.drawTierLabels(8, layouts);

    // Compute label placements
    const labelPlacements = this.computeLabelPlacements(layouts, lines);

    // Draw nodes and create hit zones
    for (const layout of layouts) {
      this.drawNode(g, layout, S, labelPlacements);
      this.createNodeZone(layout, treeName);
    }

    // Info panel — create (or reuse) and attach component refs for status display
    if (!this.infoPanel) {
      this.infoPanel = new TreeInfoPanel(this.scene, { rightX: GAME_WIDTH - 10, topY: 30 });
    }
    this.infoPanel.setComponents(attunement, skillProgression);
    this.infoPanel.showDefault(GAME_WIDTH - 10, 30);
  }

  /** Destroy all rendered objects and reset selected node state. */
  clear(): void {
    for (const obj of this.objects) obj.destroy();
    this.objects = [];
    if (this.infoPanel) {
      this.infoPanel.destroy();
      this.infoPanel = null;
    }
  }

  /** Measure text width using the canvas 2d context. */
  private measureText(text: string, fontSize: string): number {
    const ctx = this.scene.sys.game.canvas.getContext('2d');
    if (!ctx) return text.length * 5.5;
    ctx.font = `${fontSize} ${Font.family}`;
    return ctx.measureText(text).width;
  }

  private layoutNodes(
    treeDef: V4TreeDefinition,
    x: number, y: number, width: number, height: number,
    treeName: string,
    attunement: AttunementComponent,
    skillProgression: SkillProgressionComponent,
  ): NodeLayout[] {
    const engine = new TreeLayoutEngine();
    const bounds = { x, y, width, height };
    const positions = engine.layout(treeDef, bounds, (t, fs) => this.measureText(t, fs), Font.sizeWorld);

    const layouts: NodeLayout[] = [];
    const allNodes = ConfigLoader.getV4AllNodes(treeName);
    const nodeMap = new Map<string, V4NodeDefinition | V4ApotheosisDefinition>();
    for (const node of allNodes) nodeMap.set(node.id, node);

    for (const [nodeId, pos] of positions) {
      const node = nodeMap.get(nodeId);
      if (!node) continue;
      layouts.push({
        node,
        tier: pos.tier,
        x: pos.x,
        y: pos.y,
        state: this.getNodeState(node, treeName, attunement, skillProgression),
        nodeType: node.nodeType,
      });
    }

    return layouts;
  }

  private getNodeState(
    node: V4NodeDefinition | V4ApotheosisDefinition,
    treeName: string,
    attunement: AttunementComponent,
    skillProgression: SkillProgressionComponent,
  ): NodeState {
    const isUnlocked = skillProgression.isUnlocked(node.id);

    if (isUnlocked) {
      if (node.nodeType === 'passive') return 'passive';
      // Only show as equipped if this node IS the effective version —
      // a base skill replaced by an unlocked evolution shouldn't glow.
      if (attunement.isAttuned(node.id)) {
        const effective = skillProgression.getEffectiveSkill(node.id);
        if (effective === node.id) return 'equipped';
      }
      // Base skill replaced by an unlocked evolution — dim it
      if (skillProgression.getEffectiveSkill(node.id) !== node.id) return 'superseded';
      return 'unlocked';
    }

    // Check if this is a fork option that's available (must be reachable on current path)
    const forkInfo = ConfigLoader.getV4ForkForNode(node.id);
    if (forkInfo && forkInfo.treeName === treeName) {
      const tree = skillProgression.getTreeProgress(treeName);
      const fork = forkInfo.fork;
      if (!tree.forkChoices[fork.forkId]
        && node.xpThreshold <= tree.xp
        && ConfigLoader.isV4NodeReachable(treeName, fork.forkId, tree.forkChoices)) {
        return 'fork_available';
      }
    }

    return 'locked';
  }

  /** Build a map from node ID to NodeLayout for O(1) parent lookups. */
  private buildLayoutMap(layouts: NodeLayout[]): Map<string, NodeLayout> {
    const layoutMap = new Map<string, NodeLayout>();
    for (const l of layouts) layoutMap.set(l.node.id, l);
    return layoutMap;
  }

  /**
   * Walk every parent-child branch pair in the tree — both regular parentId
   * connections and apotheosis convergence parentIds — and invoke the callback
   * for each resolved (parent, child) layout pair.
   */
  private forEachBranchPair(
    layoutMap: Map<string, NodeLayout>,
    treeDef: V4TreeDefinition,
    callback: (parent: NodeLayout, child: NodeLayout) => void,
  ): void {
    // Regular nodes: each non-root parentId edge
    for (const layout of layoutMap.values()) {
      const parentId = 'parentId' in layout.node ? layout.node.parentId : undefined;
      if (!parentId || parentId === 'root') continue;
      const parent = layoutMap.get(parentId);
      if (!parent) continue;
      callback(parent, layout);
    }

    // Apotheosis convergence: multiple parentIds per node
    for (const apo of treeDef.apotheosis) {
      const apoLayout = layoutMap.get(apo.id);
      if (!apoLayout) continue;
      for (const pid of apo.parentIds) {
        const parent = layoutMap.get(pid);
        if (!parent) continue;
        callback(parent, apoLayout);
      }
    }
  }

  private drawBranches(
    g: Phaser.GameObjects.Graphics,
    layoutMap: Map<string, NodeLayout>,
    treeDef: V4TreeDefinition,
    treeXp: number,
    S: number,
  ): void {
    this.forEachBranchPair(layoutMap, treeDef, (parent, child) => {
      this.drawBranchSegment(g, S, parent.x, parent.y, child.x, child.y, treeXp, child.node.xpThreshold);
    });
  }

  /** Draw a single branch segment between two nodes with dim base + growing/grown overlay. */
  private drawBranchSegment(
    g: Phaser.GameObjects.Graphics, S: number,
    fromX: number, fromY: number, toX: number, toY: number,
    treeXp: number, xpThreshold: number,
  ): void {
    const isGrown = treeXp >= xpThreshold;
    const progress = xpThreshold > 0 ? clamp(treeXp / xpThreshold, 0, 1) : 1;

    // Dim full-extent branch
    g.lineStyle(0.5 * S, Color.treeBranch, 0.12);
    g.beginPath();
    g.moveTo(fromX * S, fromY * S);
    g.lineTo(toX * S, toY * S);
    g.strokePath();

    // Growing/grown overlay
    if (progress > 0) {
      const grownColor = isGrown ? Color.treeBranchGrown : Color.treeBranchGrowing;
      const grownAlpha = isGrown ? 0.35 : 0.25;
      g.lineStyle(1.5 * S, grownColor, grownAlpha);
      g.beginPath();
      g.moveTo(fromX * S, fromY * S);
      const endX = fromX + (toX - fromX) * progress;
      const endY = fromY + (toY - fromY) * progress;
      g.lineTo(endX * S, endY * S);
      g.strokePath();
    }
  }

  private drawTierLabels(x: number, layouts: NodeLayout[]): void {
    const tiers: LayoutTier[] = ['foundation', 'threshold', 'transcendent', 'apotheosis'];
    const tierColors: Record<string, string> = {
      foundation: Hex.treeTierFoundation,
      threshold: Hex.treeTierThreshold,
      transcendent: Hex.treeTierTranscendent,
      apotheosis: Hex.treeTierApotheosis,
    };

    for (const tier of tiers) {
      const tierLayouts = layouts.filter(l => l.tier === tier);
      if (tierLayouts.length === 0) continue;

      const avgY = tierLayouts.reduce((sum, l) => sum + l.y, 0) / tierLayouts.length;
      const label = crispText(this.scene, x, avgY, TIER_LABELS[tier], {
        fontFamily: Font.family, fontSize: Font.sizeXs, color: tierColors[tier],
      } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0, 0.5).setAlpha(NODE_TIER_LABEL_ALPHA);
      this.objects.push(label);
    }
  }

  /** Map V4 node type to the atlas frame key in the node-icons atlas. */
  private getNodeIconFrame(nodeType: string): string {
    switch (nodeType) {
      case 'passive': return 'node-icon-passive';
      case 'upgrade': return 'node-icon-upgrade';
      case 'evolution': return 'node-icon-active'; // evolution reuses the active icon
      default: return 'node-icon-active';
    }
  }

  private drawNode(
    g: Phaser.GameObjects.Graphics,
    layout: NodeLayout,
    S: number,
    labelPlacements: Map<string, LabelPlacement>,
  ): void {
    const { x, y, node, state, tier, nodeType } = layout;
    const tierColor = getTierColor(tier);
    const fillAlpha = state === 'locked' ? 0.25 : state === 'superseded' ? 0.35 : 1;
    const radius = getNodeRadius(nodeType);

    // Underlay glow for equipped active/apotheosis/evolution nodes (drawn first so icon sits on top)
    if (state === 'equipped' && nodeType !== 'passive' && nodeType !== 'upgrade') {
      const glowRadius = (radius + 4) * S;
      const glowKey = ensureGlowTexture(this.scene, glowRadius);
      const glow = this.scene.add.image(x, y, glowKey)
        .setScale(1 / S).setTint(0xffffff).setAlpha(1);
      this.objects.push(glow);
    }

    // Underlay pulse for fork_available active/apotheosis nodes
    if (state === 'fork_available' && nodeType !== 'passive' && nodeType !== 'upgrade' && nodeType !== 'evolution') {
      const ringKey = ensureRingTexture(this.scene, (radius + 2) * S, 1.5 * S);
      const pulse = this.scene.add.image(x, y, ringKey)
        .setScale(1 / S).setTint(0xffdd00).setAlpha(NODE_FORK_PULSE_ALPHA);
      this.objects.push(pulse);
    }

    // Draw node icon — sprite from atlas if available, procedural fallback otherwise
    if (this.hasNodeIcons) {
      const icon = this.scene.add.image(x, y, 'node-icons', this.getNodeIconFrame(nodeType))
        .setDisplaySize(radius * 2, radius * 2)
        .setTint(tierColor)
        .setAlpha(fillAlpha);
      this.objects.push(icon);

      // Evolution inner dot (distinguishes evolution from active)
      if (nodeType === 'evolution') {
        const dot = crispCircle(this.scene, x, y, 2, 0xffffff, fillAlpha * 0.6);
        this.objects.push(dot);
      }
    } else {
      // Procedural fallback when node-icons atlas is not loaded
      if (nodeType === 'passive') {
        g.fillStyle(tierColor, fillAlpha);
        this.fillDiamond(g, x * S, y * S, radius * S);
        g.lineStyle(1 * S, tierColor, fillAlpha);
        this.strokeDiamond(g, x * S, y * S, radius * S);
      } else if (nodeType === 'upgrade') {
        g.fillStyle(tierColor, fillAlpha);
        g.fillRect((x - radius) * S, (y - radius) * S, radius * 2 * S, radius * 2 * S);
        g.lineStyle(1 * S, tierColor, fillAlpha);
        g.strokeRect((x - radius) * S, (y - radius) * S, radius * 2 * S, radius * 2 * S);
      } else if (nodeType === 'evolution') {
        const evoCircle = crispCircle(this.scene, x, y, radius, tierColor, fillAlpha);
        this.objects.push(evoCircle);
        const dot = crispCircle(this.scene, x, y, 2, 0xffffff, fillAlpha * 0.6);
        this.objects.push(dot);
      } else {
        const activeCircle = crispCircle(this.scene, x, y, radius, tierColor, fillAlpha);
        this.objects.push(activeCircle);
      }
    }

    // Node name label — show for non-locked, non-upgrade nodes
    if (state !== 'locked' && state !== 'superseded' && nodeType !== 'upgrade' && nodeType !== 'passive') {
      const placement = labelPlacements.get(node.id);
      if (placement) {
        const fontSize = Font.sizeWorld;
        const nameText = crispText(this.scene, placement.x, placement.y, node.name, {
          fontFamily: Font.family, fontSize, color: Hex.textPrimary,
        } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(placement.originX, placement.originY);
        this.objects.push(nameText);
      }
    }
  }

  private createNodeZone(layout: NodeLayout, treeName: string): void {
    const { x, y, node, state, nodeType, tier } = layout;
    const radius = getNodeRadius(nodeType);
    const hitSize = (radius + 6) * 2;

    const zone = this.scene.add.zone(x, y, hitSize, hitSize)
      .setInteractive({ useHandCursor: state !== 'locked' });

    zone.on('pointerdown', () => {
      this.callbacks.onNodeClick(treeName, node, tier);
    });

    // Hover label for passive/upgrade nodes (active labels are always visible)
    let hoverLabel: Phaser.GameObjects.Text | null = null;
    if ((nodeType === 'passive' || nodeType === 'upgrade') && state !== 'locked') {
      zone.on('pointerover', () => {
        this.callbacks.onNodeHover(node);
        this.infoPanel?.showNodeInfo(node);
        if (!hoverLabel) {
          hoverLabel = crispText(this.scene, x, y + radius + 3, node.name, {
            fontFamily: Font.family, fontSize: Font.sizeWorld, color: Hex.textPrimary,
          } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5, 0).setDepth(2000);
          this.objects.push(hoverLabel);
        }
      });
      zone.on('pointerout', () => {
        this.infoPanel?.showDefault();
        if (hoverLabel) {
          hoverLabel.destroy();
          const idx = this.objects.indexOf(hoverLabel);
          if (idx >= 0) this.objects.splice(idx, 1);
          hoverLabel = null;
        }
      });
    } else {
      zone.on('pointerover', () => {
        this.callbacks.onNodeHover(node);
        this.infoPanel?.showNodeInfo(node);
      });
      zone.on('pointerout', () => {
        this.infoPanel?.showDefault();
      });
    }

    this.objects.push(zone);
  }

  /** Collect all branch line segments for label collision avoidance. */
  private collectBranchLines(layoutMap: Map<string, NodeLayout>, treeDef: V4TreeDefinition): LineSeg[] {
    const lines: LineSeg[] = [];
    this.forEachBranchPair(layoutMap, treeDef, (parent, child) => {
      lines.push({ x1: parent.x, y1: parent.y, x2: child.x, y2: child.y });
    });
    return lines;
  }

  /** Find best label position for each visible node. */
  private computeLabelPlacements(layouts: NodeLayout[], lines: LineSeg[]): Map<string, LabelPlacement> {
    const placements = new Map<string, LabelPlacement>();

    const nodeCircles: NodeCircle[] = layouts.map(l => ({
      x: l.x,
      y: l.y,
      r: getNodeRadius(l.nodeType),
    }));

    for (const layout of layouts) {
      const { x, y, node, state, nodeType } = layout;
      if (state === 'locked' || nodeType === 'passive' || nodeType === 'upgrade') continue;

      const radius = getNodeRadius(nodeType);
      const fontSize = Font.sizeWorld;
      const textW = this.measureText(node.name, fontSize) / CRISP_SCALE;
      const textH = parseInt(fontSize, 10) * 1.2;

      const candidates = buildLabelCandidates(x, y, radius, LABEL_GAP);

      // Exclude the owning node from collision tests so a label can hug its own node.
      const othersOnly = nodeCircles.filter(nc => nc.x !== x || nc.y !== y);
      const [px, py, pox, poy] = pickBestCandidate(candidates, textW, textH, lines, othersOnly);
      placements.set(node.id, { x: px, y: py, originX: pox, originY: poy });
    }

    return placements;
  }

  private fillDiamond(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number): void {
    g.beginPath();
    g.moveTo(cx, cy - r);
    g.lineTo(cx + r, cy);
    g.lineTo(cx, cy + r);
    g.lineTo(cx - r, cy);
    g.closePath();
    g.fillPath();
  }

  private strokeDiamond(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number): void {
    g.beginPath();
    g.moveTo(cx, cy - r);
    g.lineTo(cx + r, cy);
    g.lineTo(cx, cy + r);
    g.lineTo(cx - r, cy);
    g.closePath();
    g.strokePath();
  }
}
